// ============================================================
// MongoDBProject.jsx
//
// A branded, hands-on capstone project: "Smart Warehouse Tracker".
// Focuses on Schema Design, CRUD, and Aggregation.
//
// Brand palette:
//   Background  → #001E2B  (MongoDB Deep Navy)
//   Primary     → #00ED64  (MongoDB Spring Green)
//   Secondary   → #00684A  (Dark Green)
//   Text        → #FFFFFF  (White)
//   Code        → #010409  (GitHub Dark)
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Paper,
    Stack,
    IconButton,
    Button,
    Avatar,
    LinearProgress,
} from "@mui/material";

/* MUI Icons */
import InventoryIcon from "@mui/icons-material/Inventory";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CodeIcon from "@mui/icons-material/Code";
import SchoolIcon from "@mui/icons-material/School";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import TerminalIcon from "@mui/icons-material/Terminal";
import LayersIcon from "@mui/icons-material/Layers";
import QueryStatsIcon from "@mui/icons-material/QueryStats";

/* ── Brand tokens ────────────────────────────────────────── */
const BRAND = {
    bg: "#001E2B",
    surface: "rgba(255,255,255,0.04)",
    border: "rgba(0, 237, 100, 0.2)",
    borderHover: "rgba(0, 237, 100, 0.6)",
    green: "#00ED64",
    white: "#FFFFFF",
    muted: "rgba(255,255,255,0.6)",
    codeBg: "#010409",
};

/* ── Sub-Components ──────────────────────────────────────── */

function CodeTerminal({ code, label, color = BRAND.green }) {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Box sx={{ background: BRAND.codeBg, borderRadius: 3, border: `1px solid ${BRAND.border}`, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", background: "rgba(255,255,255,0.02)" }}>
                <Typography variant="caption" sx={{ color: color, fontWeight: 700, fontFamily: "monospace" }}>{label}</Typography>
                <IconButton size="small" onClick={handleCopy} sx={{ color: BRAND.muted }}>
                    {copied ? <DoneIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Box sx={{ p: 2.5, overflowX: "auto" }}>
                <Typography component="pre" sx={{ fontFamily: "'Fira Code', monospace", fontSize: 13, color: "#D1D5DB", lineHeight: 1.5 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

/* ── Main Component ──────────────────────────────────────── */

export default function MongoDBProject() {
    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 6, md: 10 }, color: BRAND.white }}>
            <Container maxWidth="lg">

                {/* 1. PROJECT HERO */}
                <Box sx={{ mb: 10, textAlign: { xs: "center", md: "left" } }}>
                    <Chip
                        icon={<SchoolIcon style={{ color: BRAND.green }} />}
                        label="Capstone Project · Practical Lab"
                        sx={{ bgcolor: "rgba(0,237,100,0.1)", color: BRAND.green, border: `1px solid ${BRAND.border}`, mb: 2 }}
                    />
                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        Smart <span style={{ color: BRAND.green }}>Warehouse</span> Tracker
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: 18, maxWidth: 800, lineHeight: 1.7 }}>
                        Apply your MongoDB knowledge to build a real-time inventory management system.
                        You'll design a flexible document schema, perform nested updates, and generate
                        analytics using the Aggregation Framework.
                    </Typography>
                </Box>

                {/* 2. PROJECT MILESTONES */}
                <Box sx={{ mb: 10 }}>
                    <Typography variant="subtitle2" sx={{ color: BRAND.green, mb: 1 }}>PROJECT COMPLETION ROADMAP</Typography>
                    <LinearProgress variant="determinate" value={40} sx={{ height: 8, borderRadius: 4, bgcolor: "rgba(255,255,255,0.05)", "& .MuiLinearProgress-bar": { bgcolor: BRAND.green } }} />
                    <Grid container spacing={3} sx={{ mt: 2 }}>
                        {[
                            { title: "Schema Design", icon: <LayersIcon />, desc: "Define nested BSON structure." },
                            { title: "CRUD Impl.", icon: <AssignmentIcon />, desc: "Batch insert and atomic updates." },
                            { title: "Aggregation", icon: <QueryStatsIcon />, desc: "Build the inventory dashboard logic." }
                        ].map((step, i) => (
                            <Grid item xs={12} md={4} key={i}>
                                <Paper sx={{ p: 3, bgcolor: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 4 }}>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Avatar sx={{ bgcolor: BRAND.green, color: BRAND.bg }}>{step.icon}</Avatar>
                                        <Box>
                                            <Typography variant="subtitle1" sx={{ color: BRAND.white, fontWeight: 700 }}>{step.title}</Typography>
                                            <Typography variant="caption" sx={{ color: BRAND.muted }}>{step.desc}</Typography>
                                        </Box>
                                    </Stack>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>



                {/* 3. STEP 1: THE SCHEMA */}
                <Box sx={{ mb: 10 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                        <InventoryIcon sx={{ color: BRAND.green }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>Phase 1: Designing the Document</Typography>
                    </Box>
                    <Typography sx={{ color: BRAND.muted, mb: 4, lineHeight: 1.8 }}>
                        In a Smart Warehouse, products have varying attributes. We will use an <strong>Embedded Document</strong> model
                        to keep stock levels and metadata in a single, high-speed record.
                    </Typography>
                    <CodeTerminal
                        label="mongosh: Initial Setup"
                        code={`db.products.insertOne({\n  sku: "IOT-992",\n  name: "Thermal Sensor",\n  category: "Electronics",\n  stock: {\n    warehouse_A: 50,\n    warehouse_B: 12\n  },\n  specs: { resolution: "0.1C", voltage: "3.3V" },\n  lastUpdated: new Date()\n});`}
                    />
                </Box>



                {/* 4. STEP 2: ATOMIC UPDATES */}
                <Grid container spacing={6} alignItems="center" sx={{ mb: 10 }}>
                    <Grid item xs={12} md={6}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                            <CodeIcon sx={{ color: BRAND.green }} />
                            <Typography variant="h5" sx={{ fontWeight: 800 }}>Phase 2: Inventory Logic</Typography>
                        </Box>
                        <Typography sx={{ color: BRAND.muted, mb: 3, lineHeight: 1.8 }}>
                            When an item is sold, we need to decrement the stock level. We use the <code>$inc</code> operator
                            to ensure the update is <strong>atomic</strong>—meaning no data is lost if multiple sales happen at once.
                        </Typography>
                        <Paper sx={{ p: 2, bgcolor: "rgba(0, 237, 100, 0.05)", border: `1px solid ${BRAND.green}`, borderRadius: 2 }}>
                            <Typography variant="body2" sx={{ color: BRAND.white }}>
                                <strong>Task:</strong> Update the stock for 'Warehouse A' by adding 10 units and updating the timestamp.
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <CodeTerminal
                            label="mongosh: Atomic Update"
                            code={`db.products.updateOne(\n  { sku: "IOT-992" },\n  {\n    $inc: { "stock.warehouse_A": 10 },\n    $set: { lastUpdated: new Date() }\n  }\n);`}
                        />
                    </Grid>
                </Grid>

                {/* 5. STEP 3: ANALYTICS PIPELINE */}
                <Box sx={{ mb: 10 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                        <QueryStatsIcon sx={{ color: BRAND.green }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>Phase 3: Real-time Analytics</Typography>
                    </Box>
                    <Typography sx={{ color: BRAND.muted, mb: 4 }}>
                        Calculate total stock across all warehouses for the 'Electronics' category.
                    </Typography>
                    <CodeTerminal
                        label="Aggregation Pipeline"
                        code={`db.products.aggregate([\n  { $match: { category: "Electronics" } },\n  { $group: {\n      _id: "$category",\n      totalStock: { $sum: "$stock.warehouse_A" }\n  } }\n]);`}
                    />
                </Box>



                {/* FOOTER CTA */}
                <Box sx={{
                    p: 8, borderRadius: 8, textAlign: "center",
                    background: `linear-gradient(45deg, ${BRAND.bg}, #00684A)`,
                    border: `1px solid ${BRAND.border}`,
                    boxShadow: `0 20px 40px rgba(0,0,0,0.5)`
                }}>
                    <TerminalIcon style={{ fontSize: 40, color: BRAND.green, marginBottom: 16 }} />
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Ready to Deploy?</Typography>
                    <Typography sx={{ color: BRAND.muted, mb: 4 }}>
                        Ensure your indexes are optimized before the final certification exam.
                    </Typography>
                    <Stack direction="row" spacing={2} justifyContent="center">
                        <Button variant="contained" sx={{ bgcolor: BRAND.green, color: BRAND.bg, fontWeight: 900 }}>Run Tests</Button>
                        <Button variant="outlined" sx={{ color: BRAND.white, borderColor: BRAND.muted }}>Submit Project</Button>
                    </Stack>
                </Box>

            </Container>
        </Box>
    );
}
